import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Org } from '@weave/schema-gen-ts/dist/apis/api/identity/v1/api.pb';
import { isEqual } from 'lodash-es';
import { getUser, setLoginData } from '@frontend/auth-helpers';
import type { SelectedScope } from '@frontend/scope-policy';
import { shell, useShellLocationsSyncStore } from '@frontend/shell-utils';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { useAllAccessScopeStore } from './all-access-store';
import { WeaveLocation, WeaveLocationGroup } from './types';
import { deriveGroupIdsFromSelection, resolvePreferredGroupIdFromSelection } from './utils';

export type OrgIdMap = Record<string, OrgIdMapKey>;

export type OrgIdMapKey = { parents: WeaveLocationGroup[]; locations: WeaveLocation[] };
interface ScopeStore {
  locationData: Record<string, WeaveLocation>;
  setLocationData: (scopes: Record<string, WeaveLocation>) => void;
  groupData: Record<string, WeaveLocationGroup>;
  setGroupData: (scopes: Record<string, WeaveLocationGroup>) => void;
  orgData: Record<string, Org>;
  setOrgData: (orgs: Record<string, Org>) => void;
  selectedOrgId: string;
  setSelectedOrgId: (orgId: string) => void;
  selectedGroupId: string;
  setSelectedGroupId: (groupId: string) => void;
  forcedGroupId: string;
  setForcedGroupId: (groupId: string) => void;
  selectedLocationIds: string[];
  setSelectedLocationIds: (scopes: string[]) => void;
  // selectedScope is the canonical policy-mode signal; selectedLocationIds remain compatibility state.
  selectedScope?: SelectedScope;
  setSelectedScope: (selectedScope?: SelectedScope) => void;
  appendLocationData: (scopes: Record<string, WeaveLocation>) => void;
  appendGroupData: (scopes: Record<string, WeaveLocationGroup>) => void;
  appendOrgData: (orgs: Record<string, Org>) => void;
  scopeHierarchy: Record<string, Record<string, string[]>>;
  setScopeHierarchy: (hierarchy: Record<string, Record<string, string[]>>) => void;
  appendScopeHierarchy: (hierarchy: Record<string, Record<string, string[]>>) => void;

  // Derived/computed properties — updated atomically by setters above
  accessibleLocationMap: Record<string, WeaveLocation>;
  accessibleGroupMap: Record<string, WeaveLocationGroup>;
  accessibleScopeHierarchy: Record<string, Record<string, string[]>>;
  accessibleGroupIds: string[];
  accessibleLocationIds: string[];
  accessibleOrgIds: string[];
  isSingleTypeScope: boolean;
  /**
   * All distinct group IDs represented by the current `selectedLocationIds`
   * across multiple parents in workspace mode.
   *
   * Derived atomically alongside selection changes — no manual sync needed.
   * - Workspace mode: may contain multiple groups (multi-parent selection).
   * - Legacy strict mode: naturally resolves to a single-element array.
   *
   * Use this when you need the full set of group IDs across a multi-group
   * selection. For the representative singular group ID, use `selectedGroupId`
   * or `forcedGroupId` — they handle both legacy and workspace modes.
   */
  selectedGroupIds: string[];
  setSelectionState: (selectionPatch: SelectionStatePatch) => void;
}

type SelectionStateInput = Pick<
  ScopeStore,
  'selectedLocationIds' | 'selectedGroupId' | 'forcedGroupId' | 'selectedOrgId' | 'selectedScope'
>;

type SelectionStatePatch = Partial<SelectionStateInput>;

type SelectionState = SelectionStateInput & Pick<ScopeStore, 'isSingleTypeScope' | 'selectedGroupIds'>;

type SetSelectedGroupOptions = { skipValidation?: boolean };
type SetSelectedLocationIdsOptions = { skipValidation?: boolean; onError?: (message: string) => void };

const computeAccessibleLocationMap = (locationData: Record<string, WeaveLocation>): Record<string, WeaveLocation> =>
  Object.entries(locationData).reduce((acc, [id, location]) => {
    if (location.accessible && location.active) acc[id] = { ...location };
    return acc;
  }, {} as Record<string, WeaveLocation>);

const computeAccessibleGroupMap = (groupData: Record<string, WeaveLocationGroup>): Record<string, WeaveLocationGroup> =>
  Object.entries(groupData).reduce((acc, [id, group]) => {
    if (group.accessible && group.active) {
      acc[id] = {
        ...group,
        children: group.children.filter((l) => l.accessible && l.active),
      };
    }
    return acc;
  }, {} as Record<string, WeaveLocationGroup>);

const computeAccessibleScopeHierarchy = (
  scopeHierarchy: Record<string, Record<string, string[]>>,
  accessibleLocationMap: Record<string, WeaveLocation>,
  accessibleGroupMap: Record<string, WeaveLocationGroup>
): Record<string, Record<string, string[]>> =>
  Object.entries(scopeHierarchy).reduce((orgAcc, [orgId, org]) => {
    orgAcc[orgId] = Object.entries(org).reduce((groupAcc, [groupId, locationIds]) => {
      const isGroupAccessible = !!accessibleGroupMap[groupId];
      const accessibleLocationIdsInGroup = locationIds.filter((id) => !!accessibleLocationMap[id]);
      if (isGroupAccessible || accessibleLocationIdsInGroup.length > 0) {
        groupAcc[groupId] = accessibleLocationIdsInGroup;
      }
      return groupAcc;
    }, {} as Record<string, string[]>);
    return orgAcc;
  }, {} as Record<string, Record<string, string[]>>);

const computeIsSingleTypeScope = (
  selectedLocationIds: string[],
  groupData: Record<string, WeaveLocationGroup>,
  forcedGroupId: string
): boolean => selectedLocationIds.length === 1 && groupData[forcedGroupId]?.locationType === 'single';

const computeSelectionState = (
  groupData: Record<string, WeaveLocationGroup>,
  accessibleLocationMap: Record<string, WeaveLocation>,
  accessibleGroupMap: Record<string, WeaveLocationGroup>,
  selection: SelectionStateInput
): SelectionState => ({
  ...selection,
  isSingleTypeScope: computeIsSingleTypeScope(selection.selectedLocationIds, groupData, selection.forcedGroupId),
  selectedGroupIds: deriveGroupIdsFromSelection(selection.selectedLocationIds, {
    accessibleLocationMap,
    accessibleGroupMap,
  }),
});

const getNextSelectionState = (
  state: Pick<
    ScopeStore,
    | 'groupData'
    | 'accessibleLocationMap'
    | 'accessibleGroupMap'
    | 'selectedLocationIds'
    | 'selectedGroupId'
    | 'forcedGroupId'
    | 'selectedOrgId'
    | 'selectedScope'
  >,
  selectionPatch: SelectionStatePatch
): SelectionState => {
  const hasSelectedScopePatch = Object.prototype.hasOwnProperty.call(selectionPatch, 'selectedScope');

  return computeSelectionState(state.groupData, state.accessibleLocationMap, state.accessibleGroupMap, {
    selectedLocationIds: selectionPatch.selectedLocationIds ?? state.selectedLocationIds,
    selectedGroupId: selectionPatch.selectedGroupId ?? state.selectedGroupId,
    forcedGroupId: selectionPatch.forcedGroupId ?? state.forcedGroupId,
    selectedOrgId: selectionPatch.selectedOrgId ?? state.selectedOrgId,
    selectedScope: hasSelectedScopePatch ? selectionPatch.selectedScope : state.selectedScope,
  });
};

export const createScopeStore = () =>
  createStoreWithSubscribe<ScopeStore>(
    (set, get) => ({
      locationData: {},
      setLocationData: (locations) => {
        const accessibleLocationMap = computeAccessibleLocationMap(locations);
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          get().scopeHierarchy,
          accessibleLocationMap,
          get().accessibleGroupMap
        );
        set(
          {
            locationData: locations,
            accessibleLocationMap,
            accessibleLocationIds: Object.keys(accessibleLocationMap),
            accessibleScopeHierarchy,
          },
          false,
          { type: 'setLocationData' }
        );
        useAllAccessScopeStore.getState().setAccessibleLocationMap(accessibleLocationMap);
      },
      groupData: {},
      setGroupData: (groups) => {
        const accessibleGroupMap = computeAccessibleGroupMap(groups);
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          get().scopeHierarchy,
          get().accessibleLocationMap,
          accessibleGroupMap
        );
        const isSingleTypeScope = computeIsSingleTypeScope(get().selectedLocationIds, groups, get().forcedGroupId);
        set(
          {
            groupData: groups,
            accessibleGroupMap,
            accessibleGroupIds: Object.keys(accessibleGroupMap),
            accessibleScopeHierarchy,
            isSingleTypeScope,
          },
          false,
          { type: 'setGroupData' }
        );
        useAllAccessScopeStore.getState().setAccessibleGroupMap(accessibleGroupMap);
      },
      orgData: {},
      setOrgData: (orgs) => {
        set({ orgData: orgs, accessibleOrgIds: Object.keys(orgs) }, false, { type: 'setOrgData' });
      },
      setSelectionState: (selectionPatch) => {
        const nextSelectionState = getNextSelectionState(get(), selectionPatch);
        set(nextSelectionState, false, { type: 'setSelectionState' });
      },
      selectedOrgId: '',
      setSelectedOrgId: (orgId) => {
        get().setSelectionState({ selectedOrgId: orgId });
      },
      selectedGroupId: '',
      setSelectedGroupId: (groupId) => {
        get().setSelectionState({ selectedGroupId: groupId });
      },
      forcedGroupId: '',
      setForcedGroupId: (groupId) => {
        get().setSelectionState({ forcedGroupId: groupId });
      },
      selectedLocationIds: [],
      setSelectedLocationIds: (scopes) => {
        get().setSelectionState({ selectedLocationIds: scopes });
      },
      selectedScope: undefined,
      setSelectedScope: (selectedScope) => {
        const user = getUser();
        if (user?.userID) {
          setLoginData(user.userID, { latestSelectedScope: selectedScope ?? null });
        }
        get().setSelectionState({ selectedScope });
      },
      appendLocationData: (locations) => {
        const newLocationData = { ...get().locationData, ...locations };
        const accessibleLocationMap = computeAccessibleLocationMap(newLocationData);
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          get().scopeHierarchy,
          accessibleLocationMap,
          get().accessibleGroupMap
        );
        set(
          {
            locationData: newLocationData,
            accessibleLocationMap,
            accessibleLocationIds: Object.keys(accessibleLocationMap),
            accessibleScopeHierarchy,
          },
          false,
          { type: 'appendLocationData' }
        );
        useAllAccessScopeStore.getState().setAccessibleLocationMap(accessibleLocationMap);
      },
      appendGroupData: (groups) => {
        const newGroupData = { ...get().groupData, ...groups };
        const accessibleGroupMap = computeAccessibleGroupMap(newGroupData);
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          get().scopeHierarchy,
          get().accessibleLocationMap,
          accessibleGroupMap
        );
        const isSingleTypeScope = computeIsSingleTypeScope(
          get().selectedLocationIds,
          newGroupData,
          get().forcedGroupId
        );
        set(
          {
            groupData: newGroupData,
            accessibleGroupMap,
            accessibleGroupIds: Object.keys(accessibleGroupMap),
            accessibleScopeHierarchy,
            isSingleTypeScope,
          },
          false,
          { type: 'appendGroupData' }
        );
        useAllAccessScopeStore.getState().setAccessibleGroupMap(accessibleGroupMap);
      },
      appendOrgData: (orgs) => {
        const newOrgData = { ...get().orgData, ...orgs };
        set({ orgData: newOrgData, accessibleOrgIds: Object.keys(newOrgData) }, false, { type: 'appendOrgData' });
      },
      scopeHierarchy: {},
      setScopeHierarchy: (hierarchy) => {
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          hierarchy,
          get().accessibleLocationMap,
          get().accessibleGroupMap
        );
        set({ scopeHierarchy: hierarchy, accessibleScopeHierarchy }, false, { type: 'setScopeHierarchy' });
      },
      appendScopeHierarchy: (hierarchy) => {
        const newScopeHierarchy = { ...get().scopeHierarchy, ...hierarchy };
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          newScopeHierarchy,
          get().accessibleLocationMap,
          get().accessibleGroupMap
        );
        set({ scopeHierarchy: newScopeHierarchy, accessibleScopeHierarchy }, false, {
          type: 'appendScopeHierarchy',
        });
      },

      // Derived/computed properties — initialized with defaults, updated atomically by setters above
      accessibleLocationMap: {},
      accessibleGroupMap: {},
      accessibleScopeHierarchy: {},
      accessibleGroupIds: [],
      accessibleLocationIds: [],
      accessibleOrgIds: [],
      isSingleTypeScope: false,
      selectedGroupIds: [],
    }),
    { name: 'ScopeStore', trace: true }
  );

export const useLocationGroupStore = createScopeStore();
const useScopeShallowStore = createShallowStore(useLocationGroupStore);

/**
 * Sends selected location ids to shell when they change.
 * lastSentRef must be a useRef so each component instance tracks its own "last sent" state
 * independently — avoiding the stale-closure/shared-state bug of a module-level variable.
 */
const syncLocationsToShell = ({
  selectedLocationIds,
  lastSentRef,
}: {
  selectedLocationIds: string[];
  lastSentRef: { current: string[] };
}) => {
  if (shell.isShell && !isEqual(lastSentRef.current, selectedLocationIds) && selectedLocationIds.length !== 0) {
    lastSentRef.current = selectedLocationIds ?? [];

    if (shell.featureAvailability?.has('zustand-sync')) {
      useShellLocationsSyncStore.getState().setSelectedLocationIds(selectedLocationIds ?? []);
    } else {
      shell.emit?.('send:locations', { type: 'locations', locations: selectedLocationIds ?? [] });
    }
  }
};

/**
 * Pure utility — returns a scope name lookup function given the relevant data maps.
 * Shared between useAppScopeStore and useAppScopeStoreWithSetters to avoid duplication.
 */
const computeGetScopeName =
  (
    locationData: Record<string, WeaveLocation>,
    groupData: Record<string, WeaveLocationGroup>,
    orgData: Record<string, Org>,
    accessibleLocationMap: Record<string, WeaveLocation>,
    accessibleGroupMap: Record<string, WeaveLocationGroup>
  ) =>
  (scopeId: string, type?: 'location' | 'group' | 'org'): string => {
    if (type === 'location') {
      const location = locationData[scopeId] ?? accessibleLocationMap[scopeId];
      return location?.name ?? '';
    }
    if (type === 'group') {
      const group = groupData[scopeId] ?? accessibleGroupMap[scopeId];
      return group?.name ?? '';
    }
    if (type === 'org') {
      const org = orgData[scopeId];
      return org?.name ?? '';
    }
    const scope = accessibleLocationMap[scopeId] ?? accessibleGroupMap[scopeId] ?? orgData[scopeId];
    return scope?.name ?? '';
  };

export const useAppScopeCatalog = () => {
  const {
    locationData,
    groupData,
    orgData,
    scopeHierarchy,
    accessibleLocationMap,
    accessibleGroupMap,
    accessibleScopeHierarchy,
  } = useScopeShallowStore(
    'locationData',
    'groupData',
    'orgData',
    'scopeHierarchy',
    'accessibleLocationMap',
    'accessibleGroupMap',
    'accessibleScopeHierarchy'
  );

  return useMemo(
    () => ({
      locationData,
      groupData,
      orgData,
      scopeHierarchy,
      accessibleOrgData: orgData,
      accessibleLocationData: accessibleLocationMap,
      accessibleGroupData: accessibleGroupMap,
      accessibleScopeHierarchy,
    }),
    [
      locationData,
      groupData,
      orgData,
      scopeHierarchy,
      accessibleLocationMap,
      accessibleGroupMap,
      accessibleScopeHierarchy,
    ]
  );
};

export const useAppScopeSelection = () => {
  const {
    selectedScope,
    selectedOrgId,
    selectedGroupId,
    selectedGroupIds,
    forcedGroupId,
    selectedLocationIds,
    accessibleGroupIds,
    accessibleLocationIds,
    accessibleOrgIds,
    isSingleTypeScope,
  } = useScopeShallowStore(
    'selectedScope',
    'selectedOrgId',
    'selectedGroupId',
    'selectedGroupIds',
    'forcedGroupId',
    'selectedLocationIds',
    'accessibleGroupIds',
    'accessibleLocationIds',
    'accessibleOrgIds',
    'isSingleTypeScope'
  );

  const groupInclusiveSelectedLocationIds = useMemo(() => {
    const set = new Set([...selectedLocationIds]);

    if (accessibleGroupIds.includes(selectedGroupId)) {
      set.add(selectedGroupId);
    }

    return [...set];
  }, [selectedLocationIds, accessibleGroupIds, selectedGroupId]);

  return useMemo(
    () => ({
      selectedScope,
      selectedOrgId,
      selectedGroupId,
      selectedGroupIds,
      forcedGroupId,
      selectedLocationIds,
      groupInclusiveSelectedLocationIds,
      accessibleGroupIds,
      accessibleLocationIds,
      accessibleOrgIds,
      isSingleTypeScope,
    }),
    [
      selectedScope,
      selectedOrgId,
      selectedGroupId,
      selectedGroupIds,
      forcedGroupId,
      selectedLocationIds,
      groupInclusiveSelectedLocationIds,
      accessibleGroupIds,
      accessibleLocationIds,
      accessibleOrgIds,
      isSingleTypeScope,
    ]
  );
};

export const useAppScopeNaming = () => {
  const { locationData, groupData, orgData, accessibleLocationMap, accessibleGroupMap } = useScopeShallowStore(
    'locationData',
    'groupData',
    'orgData',
    'accessibleLocationMap',
    'accessibleGroupMap'
  );

  const getScopeName = useMemo(
    () => computeGetScopeName(locationData, groupData, orgData, accessibleLocationMap, accessibleGroupMap),
    [locationData, groupData, orgData, accessibleLocationMap, accessibleGroupMap]
  );

  return useMemo(
    () => ({
      getScopeName,
      getLocationName: getScopeName,
    }),
    [getScopeName]
  );
};

/**
 *
 * Returns the scope-mode signal that determines whether the app
 * is in workspace/location/division/org mode. When undefined, no scope is
 * active (initial state, fail-open).
 */
export const useSelectedScope = () => useScopeShallowStore('selectedScope').selectedScope;

export const useAppScopeShellSync = () => {
  const selectedLocationIds = useSelectedLocationIds();
  const lastSentIdsToShellRef = useRef<string[]>([]);

  useEffect(() => {
    syncLocationsToShell({ selectedLocationIds, lastSentRef: lastSentIdsToShellRef });
  }, [selectedLocationIds]);
};

/**
 * Getter version of the app scope store - light wrapper around the Zustand store
 */
export const useAppScopeStore = () => {
  const catalog = useAppScopeCatalog();
  const selection = useAppScopeSelection();
  const naming = useAppScopeNaming();

  return useMemo(() => ({ ...catalog, ...selection, ...naming }), [catalog, selection, naming]);
};

/**
 * Setter version of the app scope store - complex wrapper with calculations and validation logic
 * This version includes setters with calculations and validation logic
 */
export const useAppScopeSetters = () => {
  const {
    setLocationData,
    setGroupData,
    setOrgData,
    setSelectionState,
    setSelectedScope,
    appendLocationData,
    appendGroupData,
    appendOrgData,
    setScopeHierarchy,
    appendScopeHierarchy,
  } = useLocationGroupStore.getState();

  const _setSelectedOrgId = useCallback(
    (orgId: string) => {
      const user = getUser();

      if (user?.userID) {
        setLoginData(user.userID, { latestOrganizationId: orgId });
      }
      setSelectionState({ selectedOrgId: orgId });
    },
    [setSelectionState]
  );

  const _setSelectedGroupId = useCallback(
    (groupId: string, { skipValidation = false }: SetSelectedGroupOptions = {}) => {
      const { accessibleGroupMap } = useLocationGroupStore.getState();
      const isGroupAccessible = !!accessibleGroupMap[groupId];

      if (skipValidation || isGroupAccessible) {
        setSelectionState({ selectedGroupId: groupId, forcedGroupId: groupId });
      } else {
        /**
         * If the group is not accessible, we still want to set the forcedGroupId.
         * There is potential for the group to not be accessible, but the locations within it are,
         * we still might need the groupId for the non-accessible group.
         */
        setSelectionState({ selectedGroupId: '', forcedGroupId: groupId });
      }
    },
    [setSelectionState]
  );

  const resetSelection = useCallback(
    (message?: string, onError?: (errorMessage: string) => void) => {
      if (message) {
        console.warn(message);
        onError?.(message);
      }

      setSelectionState({
        selectedLocationIds: [],
        selectedGroupId: '',
        forcedGroupId: '',
        selectedOrgId: '',
      });
    },
    [setSelectionState]
  );

  const _setSelectedLocationIds = useCallback(
    (locationIds: string[], { skipValidation = false, onError }: SetSelectedLocationIdsOptions = {}) => {
      const scopeState = useLocationGroupStore.getState();
      const { accessibleLocationMap, accessibleGroupMap } = scopeState;

      if (skipValidation) {
        const user = getUser();

        if (user?.userID) {
          setLoginData(user.userID, { latestLocationIds: locationIds });
        }

        // Backwards-compatible singular groupId derivation.
        // In workspace mode multiple groups may be selected — use `selectedGroupIds`
        // for multi-group-aware consumers.
        // This resolves the "first/preferred" group as a representative for
        // legacy code that expects a singular selectedGroupId / forcedGroupId.
        const preferredGroupId = resolvePreferredGroupIdFromSelection(locationIds, {
          accessibleLocationMap,
          accessibleGroupMap,
        });
        const referenceLocation = locationIds[0]
          ? // allow for parent location to be selected
            accessibleLocationMap[locationIds[0]] ?? accessibleGroupMap[locationIds[0]]
          : undefined;
        const actualGroupId = preferredGroupId ?? locationIds[0];
        const selectionPatch: SelectionStatePatch = { selectedLocationIds: locationIds };

        if (actualGroupId) {
          selectionPatch.selectedGroupId = actualGroupId;
          selectionPatch.forcedGroupId = actualGroupId;
        }
        if (referenceLocation?.orgId) {
          selectionPatch.selectedOrgId = referenceLocation.orgId;
        }

        setSelectionState(selectionPatch);

        return;
      }

      // Location ids must be accessible
      if (locationIds.some((id) => !accessibleLocationMap[id])) {
        resetSelection(
          'Setting an invalid or inaccessible location. AppScopeStore selections have been reset.',
          onError
        );
        return;
      }

      const locations = locationIds.map((id) => accessibleLocationMap[id]);
      const groupIds = new Set(locations.map((location) => location.groupId).filter(Boolean));

      if (groupIds.size > 1) {
        resetSelection('Locations must belong to the same group. AppScopeStore selections have been reset.', onError);
        return;
      }

      const user = getUser();

      if (user?.userID) {
        setLoginData(user.userID, { latestLocationIds: locationIds });
      }

      /**
       * If we can determine the orgId and groupId from a selected location,
       * set them as well
       */
      const referenceLocation = locationIds[0] ? accessibleLocationMap[locationIds[0]] : undefined;
      const groupId = referenceLocation?.groupId;
      const orgId = referenceLocation?.orgId;
      const selectionPatch: SelectionStatePatch = { selectedLocationIds: locationIds };

      if (groupId) {
        const isGroupAccessible = !!accessibleGroupMap[groupId];
        selectionPatch.forcedGroupId = groupId;
        selectionPatch.selectedGroupId = isGroupAccessible ? groupId : '';
      }

      if (orgId) {
        selectionPatch.selectedOrgId = orgId;
      }

      setSelectionState(selectionPatch);
    },
    [resetSelection, setSelectionState]
  );

  return useMemo(
    () => ({
      setLocationData,
      setGroupData,
      setOrgData,
      appendAccessibleLocationData: appendLocationData,
      appendAccessibleGroupData: appendGroupData,
      appendAccessibleOrgData: appendOrgData,
      setSelectedScope,
      setSelectedGroupId: _setSelectedGroupId,
      setSelectedOrgId: _setSelectedOrgId,
      setSelectedLocationIds: _setSelectedLocationIds,
      setScopeHierarchy,
      appendScopeHierarchy,
    }),
    [
      setLocationData,
      setGroupData,
      setOrgData,
      appendLocationData,
      appendGroupData,
      appendOrgData,
      setSelectedScope,
      _setSelectedGroupId,
      _setSelectedOrgId,
      _setSelectedLocationIds,
      setScopeHierarchy,
      appendScopeHierarchy,
    ]
  );
};

/**
 * Granular selector — subscribes only to selectedLocationIds.
 * Prefer this over useAppScopeStore() when selectedLocationIds is the only needed value.
 */
export const useSelectedLocationIds = () => useScopeShallowStore('selectedLocationIds').selectedLocationIds;

/**
 * Granular selector — subscribes only to isSingleTypeScope.
 * Prefer this over useAppScopeStore() when isSingleTypeScope is the only needed value.
 */
export const useIsSingleTypeScope = () => useScopeShallowStore('isSingleTypeScope').isSingleTypeScope;
export function useOnScopeChange(
  callback: (newLocationIds: string[] | undefined, prevLocationIds: string[] | undefined) => void,
  cleanup?: () => void
) {
  // keep a ref to the latest callback so subscribe only needs to be set up once
  const callbackRef = useRef(callback);
  const cleanupRef = useRef(cleanup);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  useEffect(() => {
    cleanupRef.current = cleanup;
  }, [cleanup]);

  useEffect(() => {
    // subscribe to changes of selectedLocationIds
    const unsubscribe = useLocationGroupStore.subscribe(
      (state) => state.selectedLocationIds,
      (newLocationIds, prevLocationIds) => {
        callbackRef.current(newLocationIds, prevLocationIds);
      }
    );
    return () => {
      // unsubscribe from changes of selectedLocationIds
      unsubscribe();
      cleanupRef.current?.();
    };
  }, []);
}
